import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  confirmForgotPasswordFan,
  forgotPasswordFan,
} from '../auth/fan-auth';
import { useI18n } from '../i18n';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { AuthShell } from './auth/AuthShell';
import { friendlyFanAuthError } from './auth/errorMessages';
import { PasswordRuleHints, PasswordMismatchHint, passwordMeetsPolicy } from './auth/PasswordRules';
import {
  errorBox,
  infoBox,
  inputClass,
  labelClass,
  primaryButton,
} from './auth/formStyles';

type Stage = 'request' | 'confirm' | 'done';

/**
 * Two-step reset on one URL: request → confirm. On step-1 success
 * we flip in place to step 2 with the email preserved, so the user
 * sees the form they need without a second navigation.
 */
export function ForgotPassword() {
  const { t } = useI18n();
  useDocumentTitle(t('auth.forgot.title'));
  const nav = useNavigate();
  const [stage, setStage] = useState<Stage>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function onRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setPending(true);
    setError(null);
    setInfo(null);
    try {
      await forgotPasswordFan(email.trim());
      setStage('confirm');
      setInfo(t('auth.forgot.codeSent'));
    } catch (err) {
      setError(friendlyFanAuthError(err, t));
    } finally {
      setPending(false);
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !code || !password) return;
    if (!passwordMeetsPolicy(password)) {
      setError(t('auth.errors.passwordTooShort'));
      return;
    }
    if (password !== passwordConfirm) {
      setError(t('auth.errors.passwordMismatch'));
      return;
    }
    setPending(true);
    setError(null);
    try {
      await confirmForgotPasswordFan(email.trim(), code.trim(), password);
      setStage('done');
    } catch (err) {
      setError(friendlyFanAuthError(err, t));
    } finally {
      setPending(false);
    }
  }

  if (stage === 'done') {
    return (
      <AuthShell
        kicker={t('auth.forgot.kicker')}
        title={t('auth.forgot.doneTitle')}
        subtitle={t('auth.forgot.doneSubtitle')}
      >
        <button type="button" onClick={() => nav('/login')} className={primaryButton}>
          {t('auth.forgot.toLogin')}
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker={t('auth.forgot.kicker')}
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.subtitle')}
      footer={
        <p className="text-center text-[11px] text-slate-500">
          <Link to="/login" className="underline hover:text-slate-300">
            {t('auth.forgot.backToLogin')}
          </Link>
        </p>
      }
      banner={info && !error ? <p className={infoBox}>{info}</p> : undefined}
    >
      {stage === 'request' && (
        <form onSubmit={onRequestCode} className="space-y-3">
          <div>
            <label htmlFor="email" className={labelClass}>{t('auth.forgot.emailLabel')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.forgot.emailPlaceholder')}
              autoComplete="email"
              autoFocus
              required
              className={inputClass}
            />
          </div>
          <button type="submit" disabled={pending} className={primaryButton}>
            {pending ? t('auth.forgot.sending') : t('auth.forgot.sendCode')}
          </button>
        </form>
      )}

      {stage === 'confirm' && (
        <form onSubmit={onConfirm} className="space-y-3">
          <div>
            <label htmlFor="email" className={labelClass}>{t('auth.forgot.emailLabel')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.forgot.emailPlaceholder')}
              autoComplete="email"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="code" className={labelClass}>{t('auth.forgot.codeLabel')}</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('auth.forgot.codePlaceholder')}
              autoComplete="one-time-code"
              autoFocus
              required
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="np" className={labelClass}>{t('auth.forgot.newPasswordLabel')}</label>
            <input
              id="np"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.forgot.newPasswordPlaceholder')}
              autoComplete="new-password"
              required
              aria-describedby="np-rules"
              className={inputClass}
            />
            <PasswordRuleHints password={password} id="np-rules" />
          </div>
          <div>
            <label htmlFor="npc" className={labelClass}>{t('auth.forgot.newPasswordConfirmLabel')}</label>
            <input
              id="npc"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className={inputClass}
            />
            <PasswordMismatchHint password={password} confirm={passwordConfirm} />
          </div>
          <button type="submit" disabled={pending} className={primaryButton}>
            {pending ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
          </button>
        </form>
      )}

      {error && <p role="alert" className={errorBox}>{error}</p>}
    </AuthShell>
  );
}
